import cms from './cms';

export interface SearchByPlateResponse {
  exists: boolean;
  property: {
    id: number;
    uid: string;
    status: string;
    type: string;
    date_status: string;
    plate: string;
    has_subscription: boolean;
  };
  customer: {
    uid: string;
    email: string;
    username: string;
    first_name: string;
    last_name: string;
    phone_number: string;
    zip_code: string;
    identification: string;
    identification_type: string;
    company: string;
    country: string;
    referer: string;
  };
  subscriptions: Array<{
    name: string;
    subscription_id: string;
    uid: string;
    date_init: string;
    date_expire: string;
    date_cancel: string | null;
    date_effective: string;
    amount: number;
    has_trial: boolean;
    trial_end: string | null;
    failed_payments: number;
    status: string;
    interval: string;
    properties: Array<{
      uid: string;
      type: string;
      status: string;
      plate: string;
      year: number;
      address_1: string;
      'zip code': string;
    }>;
  }>;
}

export interface SearchByPhoneResponse {
  customer: {
    id: number;
    uid: string;
    email: string;
    username: string;
    first_name: string;
    last_name: string;
    phone_number: string;
    zip_code: string;
    identification: string;
    identification_type: string;
    company: string;
    country: string;
    referer: string;
  };
  subscriptions: Array<{
    name: string;
    subscription_id: string;
    uid: string;
    date_init: string;
    date_expire: string;
    date_cancel: string | null;
    date_effective: string;
    amount: number;
    has_trial: boolean;
    trial_end: string | null;
    failed_payments: number;
    status: string;
    interval: string;
    properties: Array<{
      uid: string;
      type: string;
      status: string;
      plate: string;
      year: number;
      address_1: string;
      'zip code': string;
    }>;
  }>;
  services: Record<string, unknown>;
  properties: Array<{
    uid: string;
    type: string;
    status: string;
  }>;
}

// Interfaz unificada para la vista
export interface UnifiedSearchResult {
  exists: boolean;
  customer: {
    uid: string;
    email: string;
    username: string;
    first_name: string;
    last_name: string;
    phone_number: string;
    zip_code: string;
    identification: string;
    identification_type: string;
    company: string;
    country: string;
    referer: string;
  };
  subscriptions: Array<{
    name: string;
    subscription_id: string;
    uid: string;
    date_init: string;
    date_expire: string;
    date_cancel: string | null;
    date_effective: string;
    amount: number;
    has_trial: boolean;
    trial_end: string | null;
    failed_payments: number;
    status: string;
    interval: string;
    properties: Array<{
      uid: string;
      type: string;
      status: string;
      plate: string;
      year: number;
      address_1: string;
      'zip code': string;
    }>;
  }>;
  properties?: Array<{
    uid: string;
    type: string;
    status: string;
  }>;
  property?: {
    id: number;
    uid: string;
    status: string;
    type: string;
    date_status: string;
    plate: string;
    has_subscription: boolean;
  };
}

export async function searchUserByPlate(plate: string): Promise<SearchByPlateResponse> {
  try {
    const response = await cms.post<SearchByPlateResponse>('/v2/property/search-by-plate', {
      plate: plate
    });
    return response.data;
  } catch (error: unknown) {
    if (error && typeof error === 'object' && 'response' in error) {
      const axiosError = error as { response?: { data?: { message?: string } } };
      throw new Error(axiosError.response?.data?.message || 'Error al buscar por placa');
    }
    throw new Error('Error inesperado. Por favor, inténtalo de nuevo.');
  }
}

export async function searchUserByPhone(phone: string): Promise<SearchByPhoneResponse> {
  try {
    const response = await cms.post<SearchByPhoneResponse>('/v2/user/search', {
      phone: phone
    });
    return response.data;
  } catch (error: unknown) {
    if (error && typeof error === 'object' && 'response' in error) {
      const axiosError = error as { response?: { data?: { message?: string } } };
      throw new Error(axiosError.response?.data?.message || 'Error al buscar por teléfono');
    }
    throw new Error('Error inesperado. Por favor, inténtalo de nuevo.');
  }
}

function sortSubscriptionsByStatus<T extends { status: string }>(subscriptions: T[]): T[] {
  return subscriptions.sort((a, b) => {
    // Definir el orden de prioridad de estados
    const statusOrder = {
      'activa': 1,
      'active': 1,
      'pendiente': 2,
      'pending': 2,
      'cancelada': 3,
      'cancelled': 3,
      'canceled': 3
    };
    
    const statusA = a.status.toLowerCase();
    const statusB = b.status.toLowerCase();
    
    const orderA = statusOrder[statusA as keyof typeof statusOrder] || 999;
    const orderB = statusOrder[statusB as keyof typeof statusOrder] || 999;
    
    return orderA - orderB;
  });
}

// Función auxiliar para normalizar datos de búsqueda por placa
export function normalizePlateResult(data: SearchByPlateResponse): UnifiedSearchResult {
  return {
    exists: data.exists,
    customer: data.customer,
    subscriptions: sortSubscriptionsByStatus(data.subscriptions),
    property: data.property
  };
}

// Función auxiliar para normalizar datos de búsqueda por teléfono
export function normalizePhoneResult(data: SearchByPhoneResponse): UnifiedSearchResult {
  return {
    exists: true, // La búsqueda por teléfono siempre retorna datos si es exitosa
    customer: data.customer,
    subscriptions: sortSubscriptionsByStatus(data.subscriptions),
    properties: data.properties
  };
}
